import type { DataIssue, IndicatorSeries, Observation } from "../../types";
import { applyFreshness } from "../freshness.ts";

export const FED_SERIES = {
  lower: { id: "fed_target_lower", itemCode: "targetRateFrom", name: "Fed 목표금리 하단", shortName: "Fed lower" },
  upper: { id: "fed_target_upper", itemCode: "targetRateTo", name: "Fed 목표금리 상단", shortName: "Fed upper" },
  midpoint: { id: "fed_target_mid", itemCode: "MID", name: "Fed 목표금리 중간값", shortName: "Fed mid" }
} as const;

interface NyFedRateRow {
  effectiveDate?: string | null;
  targetRateFrom?: string | number | null;
  targetRateTo?: string | number | null;
  type?: string | null;
}

interface NyFedEnvelope { refRates?: NyFedRateRow[]; }
interface NyFedProxyResponse { ok: boolean; data?: NyFedEnvelope; error?: string; warning?: string; historyComplete?: boolean; collectedAt?: string; sourceUrl?: string; }

function numberOrNull(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(String(value).trim());
  return Number.isFinite(n) ? n : null;
}

export function parseNyFedTargetRange(data: NyFedEnvelope): { lower: Observation[]; upper: Observation[] } {
  const rows = data.refRates;
  if (!Array.isArray(rows) || !rows.length) throw new Error("NY Fed EFFR response contained no refRates rows");
  const lowerMap = new Map<string, number | null>();
  const upperMap = new Map<string, number | null>();
  for (const row of rows) {
    if (row.type && row.type.trim().toUpperCase() !== "EFFR") continue;
    const date = (row.effectiveDate ?? "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
    const lo = numberOrNull(row.targetRateFrom);
    const hi = numberOrNull(row.targetRateTo);
    const existingLo = lowerMap.get(date);
    const existingHi = upperMap.get(date);
    if (lowerMap.has(date) && existingLo !== lo) throw new Error(`NY Fed returned conflicting lower target values for ${date}`);
    if (upperMap.has(date) && existingHi !== hi) throw new Error(`NY Fed returned conflicting upper target values for ${date}`);
    lowerMap.set(date, lo);
    upperMap.set(date, hi);
  }
  const dates = Array.from(new Set([...lowerMap.keys(), ...upperMap.keys()])).sort();
  const lower = dates.map((date) => ({ date, value: lowerMap.get(date) ?? null }));
  const upper = dates.map((date) => ({ date, value: upperMap.get(date) ?? null }));
  if (!lower.some((o) => o.value != null) || !upper.some((o) => o.value != null)) {
    throw new Error("NY Fed EFFR response did not contain targetRateFrom/targetRateTo values");
  }
  return { lower, upper };
}

async function fetchNyFed(signal?: AbortSignal) {
  const end = new Date().toISOString().slice(0, 10);
  const response = await fetch(`/api/nyfed?start=2010-01-01&end=${end}`, { headers: { Accept: "application/json" }, signal });
  const body = await response.json() as NyFedProxyResponse;
  if (!response.ok || !body.ok || !body.data) throw new Error(body.error || `NY Fed proxy error (${response.status})`);
  return { ...parseNyFedTargetRange(body.data), collectedAt: body.collectedAt, sourceUrl: body.sourceUrl, warning: body.warning, historyComplete: body.historyComplete };
}

function rawSeries(
  spec: typeof FED_SERIES.lower | typeof FED_SERIES.upper,
  observations: Observation[],
  collectedAt?: string,
  sourceUrl?: string
): IndicatorSeries {
  return {
    id: spec.id,
    name: spec.name,
    shortName: spec.shortName,
    unit: "%",
    layer: "policy",
    source: {
      provider: "Federal Reserve Bank of New York",
      dataset: `EFFR reference-rate data · ${spec.itemCode}`,
      url: sourceUrl ?? "https://www.newyorkfed.org/markets/reference-rates/effr",
      frequency: "daily",
      quality: observations.some((o) => o.value != null) ? "official" : "unavailable",
      observationDate: [...observations].reverse().find((o) => o.value != null)?.date,
      collectedAt,
      itemCode: spec.itemCode,
      itemName: spec.name,
      nativeUnit: "%",
      note: "NY Fed EFFR reference-rate record의 FOMC target-rate/range 하단·상단 필드를 사용합니다. EFFR 자체가 아니라 정책 목표범위입니다."
    },
    observations
  };
}

function midpoint(lower: IndicatorSeries, upper: IndicatorSeries): IndicatorSeries {
  const upperMap = new Map(upper.observations.map((o) => [o.date, o.value]));
  const observations = lower.observations.map((o) => {
    const u = upperMap.get(o.date) ?? null;
    return { date: o.date, value: o.value == null || u == null ? null : Number(((o.value + u) / 2).toFixed(4)) };
  });
  const quality = lower.source.quality === "unavailable" || upper.source.quality === "unavailable"
    ? "unavailable"
    : lower.source.quality === "stale" || upper.source.quality === "stale"
      ? "stale"
      : "official";
  return {
    id: FED_SERIES.midpoint.id,
    name: FED_SERIES.midpoint.name,
    shortName: FED_SERIES.midpoint.shortName,
    unit: "%",
    layer: "policy",
    source: {
      provider: "Calculated from Federal Reserve Bank of New York",
      dataset: "(targetRateFrom + targetRateTo) / 2",
      url: "https://www.newyorkfed.org/markets/reference-rates/effr",
      frequency: "daily",
      quality,
      observationDate: [...observations].reverse().find((o) => o.value != null)?.date,
      collectedAt: [lower.source.collectedAt, upper.source.collectedAt].filter(Boolean).sort().at(-1),
      sourceIds: [lower.id, upper.id],
      nativeUnit: "%",
      note: "Target-range midpoint는 국가간 정책금리 비교를 위한 산술값이며 EFFR 자체가 아닙니다."
    },
    observations
  };
}

function unavailable(spec: typeof FED_SERIES.lower | typeof FED_SERIES.upper, note: string): IndicatorSeries {
  return {
    id: spec.id, name: spec.name, shortName: spec.shortName, unit: "%", layer: "policy",
    source: {
      provider: "Federal Reserve Bank of New York",
      dataset: `EFFR reference-rate data · ${spec.itemCode}`,
      url: "https://www.newyorkfed.org/markets/reference-rates/effr",
      frequency: "daily",
      quality: "unavailable",
      note
    },
    observations: []
  };
}

export async function loadFedTargetRange(signal?: AbortSignal): Promise<{ series: IndicatorSeries[]; issues: DataIssue[] }> {
  const issues: DataIssue[] = [];
  try {
    const data = await fetchNyFed(signal);
    const lowerFresh = applyFreshness(rawSeries(FED_SERIES.lower, data.lower, data.collectedAt, data.sourceUrl));
    const upperFresh = applyFreshness(rawSeries(FED_SERIES.upper, data.upper, data.collectedAt, data.sourceUrl));
    if (lowerFresh.issue) issues.push(lowerFresh.issue);
    if (upperFresh.issue) issues.push(upperFresh.issue);
    if (data.warning) issues.push({ id: "fed-target-range-upstream-warning", severity: "warning", scope: "fed-policy", message: data.warning });
    const earliest = data.lower.find((o) => o.value != null)?.date;
    if (data.historyComplete !== false && earliest && earliest > "2010-02-28") {
      issues.push({
        id: "fed-target-range-history-coverage",
        severity: "warning",
        scope: "fed-policy",
        message: `NY Fed historical response begins at ${earliest} although 2010-01-01 was requested; recent target range is usable but long-history policy spread may be truncated.`
      });
    }
    return { series: [lowerFresh.series, upperFresh.series, midpoint(lowerFresh.series, upperFresh.series)], issues };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    issues.push({ id: "fed-target-range", severity: "warning", scope: "fed-policy", message });
    const lower = unavailable(FED_SERIES.lower, message);
    const upper = unavailable(FED_SERIES.upper, message);
    return { series: [lower, upper, midpoint(lower, upper)], issues };
  }
}
